import React from "react";
import { Helmet } from "react-helmet-async";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Sparkles, ArrowUpRight, Lock, ShieldCheck, FileText, Globe } from "lucide-react";
import Logo from "@ui/Logo";
import { useBluelagoon } from "@bluelagoon";
import { useAuth } from "../context/AuthContext";
import InternalLink from "../components/InternalLink";
import AuthLanguagePicker from "./AuthLanguagePicker";
import AuthFaq from "./AuthFaq";
import { track, trackSignup } from "@crm/analytics";
import { generateDeviceFingerprint } from "../utils/deviceFingerprint";
import SignupV3Card from "./SignupV3Card";
import { getLastLoginMethod, setLastLoginMethod } from "./lastLoginMethod";
import { markOAuthFlight } from "./loginOauthFlight";

function SignupV3({ isShopifySignup = false }) {
    const { t, i18n } = useTranslation();
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const referralCode = searchParams.get("ref");

    const savedOtpState = React.useMemo(() => {
        try {
            const raw = sessionStorage.getItem("signup_otp_state");
            if (!raw) return null;
            const parsed = JSON.parse(raw);
            const elapsed = Math.floor((Date.now() - parsed.codeRequestedAt) / 1000);
            if (elapsed > 300) {
                sessionStorage.removeItem("signup_otp_state");
                return null;
            }
            return { ...parsed, elapsed };
        } catch {
            return null;
        }
    }, []);

    const [email, setEmail] = React.useState(savedOtpState?.email || "");
    const [isTokenStep, setIsTokenStep] = React.useState(!!savedOtpState);
    const [error, setError] = React.useState(null);
    const [successMessage, setSuccessMessage] = React.useState(
        savedOtpState?.successMessage || null
    );
    const [isResending, setIsResending] = React.useState(false);
    const [isGoogleLoading, setIsGoogleLoading] = React.useState(false);
    const [isMicrosoftLoading, setIsMicrosoftLoading] = React.useState(false);
    const [redirectedToIntegrations, setRedirectedToIntegrations] =
        React.useState(false);
    const [lastUsedMethod] = React.useState(() => getLastLoginMethod());

    const { getUser, setToken, isLoadingUser, user } = useAuth();

    const { call: signup, error: signupError, isLoading: isSignupLoading } =
        useBluelagoon({ method: "signup" });
    const { call: login, error: loginError, isLoading: isLoginLoading } =
        useBluelagoon({ method: "login" });
    const { call: requestCode, error: requestCodeError } =
        useBluelagoon({ method: "userRequestCode" });
    const { call: getGoogleUrl } = useBluelagoon({ method: "getGoogleAuthUrlForLogin" });
    const { call: getMicrosoftUrl } = useBluelagoon({ method: "getMicrosoftAuthUrlForLogin" });

    React.useEffect(() => {
        window.scrollTo({ top: 0, left: 0, behavior: "instant" });
    }, []);

    React.useEffect(() => {
        track("login_page_visited", {
            page: "signup",
            variant: "v3",
            referrer: document.referrer || "direct",
        });
    }, []);

    const handleGetUser = React.useCallback(() => {
        return getUser()
            .then((u) => {
                track("login_user_retrieved", {
                    userId: u?.id,
                    userRoles: u?.roles,
                    tutorialCompleted: u?.tutorials?.welcome?.completed,
                    isClient: u?.roles?.includes("client"),
                    source: "signup",
                });
                return u;
            })
            .catch((err) => {
                track("login_user_retrieval_failed", {
                    error: err?.message || "Unknown",
                    source: "signup",
                });
            });
    }, [getUser]);

    const handleSignupSubmit = async (event) => {
        event.preventDefault();
        const formData = Object.fromEntries(new FormData(event.target).entries());
        if (!formData.terms) {
            track("login_failed", {
                method: "email_signup",
                email: formData.email,
                error: "Terms not accepted",
            });
            setError(t("auth.signupV3.termsRequired"));
            return;
        }

        const fullName = formData.fullName || "";
        const nameParts = fullName.trim().split(/\s+/);
        const firstName = nameParts[0] || "";
        const lastName =
            nameParts.length > 1 ? nameParts.slice(1).join(" ") : "no last name";

        const payload = {
            ...formData,
            firstName,
            lastName,
            language: i18n.language,
            ...(referralCode && { referralCode }),
            deviceFingerprint: generateDeviceFingerprint(),
        };

        track("login_attempt_started", {
            method: "email_signup",
            email: payload.email,
            firstName,
            lastName,
        });

        try {
            const response = await signup(payload);
            const message =
                response?.message || t("auth.signupV3.codeSentDefault");
            localStorage.setItem("hasCompletedTutorial", "false");
            setIsTokenStep(true);
            setError(null);
            setSuccessMessage(message);
            sessionStorage.setItem(
                "signup_otp_state",
                JSON.stringify({
                    email: payload.email,
                    codeRequestedAt: Date.now(),
                    successMessage: message,
                })
            );
            trackSignup(payload.email);
            track("login_code_requested", {
                method: "email_signup",
                email: payload.email,
            });
        } catch (err) {
            track("login_code_request_failed", {
                method: "email_signup",
                email: payload.email,
                error: err?.message || err?.error || "Unknown",
            });
        }
    };

    const handleTokenSubmit = async (event) => {
        event.preventDefault();
        if (isLoginLoading) return;
        const credentials = Object.fromEntries(new FormData(event.target).entries());
        credentials.email = email;
        track("login_token_submitted", {
            method: "email_signup",
            email: credentials.email,
        });
        try {
            const response = await login({
                ...credentials,
                deviceFingerprint: generateDeviceFingerprint(),
            });
            setToken(response?.accessToken);
            sessionStorage.removeItem("signup_otp_state");
            setLastLoginMethod("email");
            if (!response?.user?.hasCompletedTutorial) {
                localStorage.setItem("hasCompletedTutorial", "false");
            }
            track("login_success", {
                method: "email_signup",
                email: credentials.email,
            });
            await handleGetUser();
            if (isShopifySignup) {
                setRedirectedToIntegrations(true);
                navigate("/integrate", {
                    state: {
                        openNewIntegrationModal: true,
                        preselectIntegrationTypeId: "shopify",
                    },
                });
            }
        } catch (err) {
            track("login_failed", {
                method: "email_signup",
                email: credentials.email,
                error: err?.message || err?.error || "Unknown",
            });
        }
    };

    const handleResendCode = async () => {
        if (isResending) return;
        setIsResending(true);
        track("login_attempt_started", { email, method: "email_signup_resend" });
        try {
            const response = await requestCode({ email });
            const message =
                response?.message || t("auth.signupV3.codeSentDefault");
            setSuccessMessage(message);
            sessionStorage.setItem(
                "signup_otp_state",
                JSON.stringify({
                    email,
                    codeRequestedAt: Date.now(),
                    successMessage: message,
                })
            );
            track("login_code_requested", {
                email,
                method: "email_signup_resend",
            });
        } catch (err) {
            setError(err);
            track("login_code_request_failed", {
                email,
                method: "email_signup_resend",
                error: err?.message || "Unknown",
            });
        } finally {
            setIsResending(false);
        }
    };

    const handleGoogleSignUp = async () => {
        setIsGoogleLoading(true);
        track("login_attempt_started", { method: "google_signup" });
        try {
            const response = await getGoogleUrl({
                isAuth: true,
                isShopifySignup,
                language: i18n.language,
                ...(referralCode && { referralCode }),
            });
            if (response.status === 200) {
                localStorage.setItem("hasCompletedTutorial", "false");
                setLastLoginMethod("google");
                markOAuthFlight("google");
                track("google_auth_url_generated", { method: "google_signup" });
                window.location.href = response.data.url;
            } else {
                setError(response.data.error);
                track("google_auth_url_failed", {
                    method: "google_signup",
                    error: response.data.error,
                });
            }
        } catch (err) {
            setError(err);
            track("login_failed", {
                method: "google_signup",
                error: err?.message || "Unknown",
            });
        } finally {
            setIsGoogleLoading(false);
        }
    };

    const handleMicrosoftSignUp = async () => {
        setIsMicrosoftLoading(true);
        track("login_attempt_started", { method: "microsoft_signup" });
        try {
            const response = await getMicrosoftUrl({
                isAuth: true,
                isShopifySignup,
                language: i18n.language,
                ...(referralCode && { referralCode }),
            });
            if (response.status === 200) {
                localStorage.setItem("hasCompletedTutorial", "false");
                setLastLoginMethod("microsoft");
                markOAuthFlight("microsoft");
                track("microsoft_auth_url_generated", { method: "microsoft_signup" });
                window.location.href = response.data.url;
            } else {
                setError(response.data.error);
                track("microsoft_auth_url_failed", {
                    method: "microsoft_signup",
                    error: response.data.error,
                });
            }
        } catch (err) {
            setError(err);
            track("login_failed", {
                method: "microsoft_signup",
                error: err?.message || "Unknown",
            });
        } finally {
            setIsMicrosoftLoading(false);
        }
    };

    React.useEffect(() => {
        if (redirectedToIntegrations) return;
        if (!user || !user.id) return;
        const hasCompletedTutorial =
            user.tutorials?.welcome?.completed ||
            localStorage.getItem("hasCompletedTutorial") === "true";
        track("login_already_authenticated", {
            userId: user.id,
            userRoles: user.roles,
            isClient: user.roles.includes("client"),
            tutorialCompleted: hasCompletedTutorial,
            source: "signup_page",
        });
        if (user.roles.includes("client")) {
            track("login_redirect", {
                destination: "/client/workflows",
                userType: "client",
                source: "signup_page",
            });
            navigate("/client/workflows");
        } else {
            track("login_redirect", {
                destination: "/workflows",
                userType: hasCompletedTutorial ? "returning_user" : "new_user",
                source: "signup_page",
            });
            navigate("/chat");
        }
    }, [user, navigate, redirectedToIntegrations]);

    const trustChips = [
        { icon: Lock, label: t("auth.loginV3.trust.sso") },
        { icon: ShieldCheck, label: t("auth.loginV3.trust.soc2") },
        { icon: FileText, label: t("auth.loginV3.trust.auditLogs") },
        { icon: Globe, label: t("auth.loginV3.trust.gdpr") },
    ];

    return (
        <div className="relative min-h-screen bg-white text-[#171a1f] font-sans antialiased overflow-x-hidden">
            <Helmet>
                <title>{`${t("auth.signup.title")} - AgentUI`}</title>
                <link rel="preconnect" href="https://fonts.googleapis.com" />
                <link rel="preconnect" href="https://fonts.gstatic.com" crossOrigin="" />
                <link
                    href="https://fonts.googleapis.com/css2?family=Bricolage+Grotesque:opsz,wght@12..96,200;12..96,300;12..96,400;12..96,500;12..96,600&display=swap"
                    rel="stylesheet"
                />
                <style>{`
                    .font-display { font-family: 'Bricolage Grotesque', -apple-system, BlinkMacSystemFont, system-ui, sans-serif; }
                    @keyframes cardIn { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: translateY(0); } }
                    @media (prefers-reduced-motion: reduce) { .animate-\\[cardIn_420ms_cubic-bezier\\(0\\.22\\,1\\,0\\.36\\,1\\)_both\\] { animation: none !important; } }
                `}</style>
            </Helmet>

            <div className="pointer-events-none fixed inset-0 z-0 overflow-hidden" aria-hidden="true">
                <div
                    className="absolute -top-32 -right-44 w-[720px] h-[720px] rounded-full opacity-70 blur-[80px]"
                    style={{ background: "radial-gradient(circle, #d9c8ee 0%, rgba(217,200,238,0) 70%)" }}
                />
                <div
                    className="absolute -bottom-52 -right-20 w-[580px] h-[580px] rounded-full opacity-55 blur-[80px]"
                    style={{ background: "radial-gradient(circle, #f3d6e4 0%, rgba(243,214,228,0) 70%)" }}
                />
            </div>

            <div className="relative z-10 grid grid-rows-[auto_1fr_auto] min-h-screen px-5 pb-7 pt-5 md:px-8 md:pb-8 md:pt-7">
                <header className="w-full max-w-[1180px] mx-auto">
                    <nav
                        aria-label="Primary"
                        className="flex items-center justify-between gap-4 rounded-full bg-white border border-[#f1eff5] shadow-[0_1px_2px_rgb(23_26_31/0.04),0_6px_16px_-6px_rgb(23_26_31/0.08)] py-2.5 pl-5 pr-3 md:py-3 md:pl-6 md:pr-3.5"
                    >
                        <a href="/" aria-label="AgentUI home" className="inline-flex items-center text-[#171a1f]">
                            <Logo className="h-7 w-auto" />
                        </a>
                        <div className="flex items-center gap-2">
                            <a
                                href="https://help.agentui.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="hidden sm:inline-flex rounded-full px-3.5 py-2 text-sm text-[#6b7280] hover:text-[#171a1f] hover:bg-[#fafafa] transition-colors"
                            >
                                {t("auth.loginV3.nav.needHelp")}
                            </a>
                            <a
                                href="https://agentui.ai"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-1.5 rounded-full border border-[#e9e7ee] py-2 pl-3.5 pr-4 text-sm font-medium text-[#171a1f] hover:bg-[#fafafa] hover:border-[#d8d4e0] transition-colors"
                            >
                                {t("auth.loginV3.nav.backToSite")}
                                <ArrowUpRight className="w-3.5 h-3.5" strokeWidth={1.6} />
                            </a>
                        </div>
                    </nav>
                </header>

                <main className="flex items-center justify-center py-14 md:py-22">
                    <div className="w-full max-w-[480px] flex flex-col items-stretch">
                        <span className="self-center inline-flex items-center gap-2 rounded-full border border-[#e8def4] bg-[#f3eff9] py-1.5 pl-3 pr-3.5 text-[13px] text-[#7c6bb0] mb-7">
                            <Sparkles className="w-3.5 h-3.5" strokeWidth={1.8} />
                            {t("auth.signupV3.eyebrow")}
                        </span>
                        <h1 className="font-display text-center text-[clamp(40px,6vw,60px)] leading-[1.04] tracking-[-0.025em] font-light text-[#171a1f] mb-3.5 text-balance">
                            {t("auth.signupV3.titleStart")}
                            <span className="block font-light text-[#a594c8]">
                                {t("auth.signupV3.titleAccent")}
                            </span>
                        </h1>
                        <p className="text-center text-[15.5px] leading-[1.55] text-[#6b7280] max-w-[36ch] mx-auto mb-9">
                            {t("auth.signupV3.lede")}
                        </p>

                        <SignupV3Card
                            isTokenStep={isTokenStep}
                            email={email}
                            setEmail={setEmail}
                            error={error}
                            successMessage={successMessage}
                            referralCode={referralCode}
                            onSignupSubmit={handleSignupSubmit}
                            onTokenSubmit={handleTokenSubmit}
                            onGoogle={handleGoogleSignUp}
                            onMicrosoft={handleMicrosoftSignUp}
                            onResend={handleResendCode}
                            isGoogleLoading={isGoogleLoading}
                            isMicrosoftLoading={isMicrosoftLoading}
                            isSignupLoading={isSignupLoading}
                            isLoadingUser={isLoadingUser}
                            isLoginLoading={isLoginLoading}
                            isResending={isResending}
                            loginError={loginError}
                            requestCodeError={requestCodeError}
                            signupError={signupError}
                            lastUsedMethod={lastUsedMethod}
                        />

                        <p className="mt-5 text-center text-[13.5px] text-[#6b7280]">
                            {t("auth.signup.alreadyHaveAccount")}{" "}
                            <InternalLink
                                to="/"
                                className="font-medium text-[#171a1f] border-b-[1.5px] border-[#a594c8] pb-px hover:border-[#7c6bb0] transition-colors"
                            >
                                {t("auth.signup.signIn")}
                            </InternalLink>
                        </p>

                        <div className="flex flex-wrap justify-center gap-2 mt-7" role="list" aria-label="Security and compliance">
                            {trustChips.map(({ icon: Icon, label }) => (
                                <span
                                    key={label}
                                    role="listitem"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white border border-[#f1eff5] shadow-[0_1px_2px_rgb(23_26_31/0.03)] py-1.5 pl-3 pr-3.5 text-[12.5px] text-[#6b7280]"
                                >
                                    <Icon className="w-3 h-3 text-[#7c6bb0] flex-shrink-0" strokeWidth={1.8} />
                                    {label}
                                </span>
                            ))}
                        </div>

                        <AuthLanguagePicker />

                        <AuthFaq />
                    </div>
                </main>

                <footer className="max-w-[760px] w-full mx-auto py-4 px-2 text-xs text-[#9ca3af] text-center">
                    <div className="flex flex-wrap justify-center items-center gap-x-4 gap-y-2">
                        <a href="https://agentui.ai/en/privacy" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.privacy")}
                        </a>
                        <span className="opacity-50">·</span>
                        <a href="https://agentui.ai/en/terms" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.terms")}
                        </a>
                        <span className="opacity-50">·</span>
                        <a href="https://app.agentui.ai/status" target="_blank" rel="noopener noreferrer" className="hover:text-[#6b7280] transition-colors">
                            {t("auth.loginV3.footer.status")}
                        </a>
                    </div>
                    <p className="mt-3 max-w-[640px] mx-auto leading-5 text-[#9ca3af]">
                        {t("auth.loginV3.footer.aiServices")}
                    </p>
                    <p className="mt-2">
                        {t("auth.loginV3.footer.copyright", { year: new Date().getFullYear() })}
                    </p>
                </footer>
            </div>
        </div>
    );
}

export default SignupV3;
